/**
 * Rôle EFFECTIF pour la navigation et la palette — par ESPACE DE TRAVAIL, pas globalement.
 *
 * DÉFAUT MESURÉ (v030-po-ux-01) : `session.roles` vient de `realm_access.roles` du jeton
 * Keycloak — un rôle GLOBAL au compte, décodé une fois à la connexion. L'autorisation
 * réellement appliquée par le serveur (`WorkspaceMembershipGuard.resolveWorkspaceRole`,
 * `specfuse-api-gateway`) résout au contraire le rôle PAR (organisation, espace, utilisateur)
 * depuis `workspace_members`, à chaque requête. Les deux ne sont PAS le même rôle : ajouter
 * un membre à un espace (`WorkspaceController.addMember`) n'écrit jamais dans Keycloak — c'est
 * une décision de périmètre explicite du contrôleur, pas un oubli. Un utilisateur propriétaire
 * de SON PROPRE espace (rôle de session `workspace_owner`) mais simple contributeur ou lecteur
 * d'un AUTRE espace auquel il a été invité voit donc, sans ce correctif, la navigation et la
 * palette de SON rôle global — pas celui, moindre, que le serveur applique réellement pour
 * l'espace ouvert. Un clic sur une entrée ainsi affichée à tort est accepté par l'écran et
 * refusé par le serveur (403) : la même famille de défaut que le câblage JdbcTemplate de
 * cette nuit — l'écran dit une chose, la base en fait une autre.
 *
 * CE QUE CE CORRECTIF NE COUVRE PAS, DÉLIBÉRÉMENT (hors périmètre de cette carte, signalé) :
 * `canWrite`/`canManageConfiguration` (auth/session.ts) restent basées sur `session.roles`
 * pour les boutons D'ACTION à l'intérieur des écrans (pas la navigation/palette elle-même) —
 * les migrer aussi vers le rôle effectif par espace est un chantier plus large que « rendre
 * cohérents contexte/rôle/navigation/palette », qui ne les nomme pas explicitement.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { Session } from '@/auth/session'

/**
 * Traduit un rôle RÉEL de `workspace_members` (`owner|contributor|viewer`, sans préfixe —
 * `WorkspaceRole.java`) vers les pseudo-rôles préfixés que `itemVisiblePourRoles`/
 * `itemsDuGroupePourMode` savent déjà lire (`workspace_owner`/`workspace_contributor`),
 * sans changer leur signature ni celle de `NAV_GROUPS`.
 */
export function rolesEffectifsPourEspace(
  sessionRoles: string[],
  membreRole: string | null | undefined,
): string[] {
  const estAdminGlobal = sessionRoles.includes('admin')
  if (membreRole == null) {
    // Aucune ligne d'appartenance connue pour CET espace (pas encore chargée, ou aucun
    // espace sélectionné) : on retombe sur le rôle de session plutôt que de tout masquer
    // pendant le chargement — mais jamais au point de perdre un admin global réel.
    return estAdminGlobal ? [...new Set([...sessionRoles, 'admin'])] : sessionRoles
  }
  const roles: string[] = []
  if (membreRole === 'owner') roles.push('workspace_owner')
  if (membreRole === 'owner' || membreRole === 'contributor') roles.push('workspace_contributor')
  // Un lecteur réel de l'espace (`membreRole === 'viewer'`) ne déclenchait aucun push : le
  // tableau retombait vide. `itemVisiblePourRoles` s'en accommode (aucune entrée n'exige la
  // présence de `workspace_viewer`), mais `UserMenu.roleLabel()` la cherche explicitement pour
  // afficher « Lecteur » — sans elle il retombe sur « Rôle inconnu », visible par CHAQUE
  // lecteur réel d'un espace (v030-dix-sept-echecs-ui-restants-tries-par-nature).
  if (membreRole === 'viewer') roles.push('workspace_viewer')
  if (estAdminGlobal) roles.push('admin')
  // v05x-secu-acces : `org_admin` et `auditor` sont des rôles d'ORGANISATION, pas de projet — la
  // passerelle les juge sans regarder le projet ouvert. Les retirer ici masquait « Mon organisation »,
  // « Utilisateurs », « Accès »… à un administrateur d'organisation dès qu'il était membre du projet
  // actif, alors que le serveur les lui ouvre.
  for (const roleOrganisation of ['org_admin', 'auditor']) {
    if (sessionRoles.includes(roleOrganisation)) roles.push(roleOrganisation)
  }
  return roles
}

/**
 * Rôles à utiliser pour la navigation et la palette de CET espace — jamais `session.roles`
 * directement pour ces deux usages. Retombe sur `session.roles` tant que la liste des
 * membres n'a pas encore répondu ou qu'aucun espace n'est sélectionné.
 */
export function useEffectiveNavRoles(session: Session, workspaceId: string | null | undefined): string[] {
  const membres = useQuery({
    queryKey: ['workspace-members-role-effectif', workspaceId],
    queryFn: () => api.listWorkspaceMembers(workspaceId as string),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  })

  const monRole = workspaceId
    ? (membres.data?.find((m) => m.user_id === session.userId)?.role ?? null)
    : undefined

  return useMemo(
    () => rolesEffectifsPourEspace(session.roles, monRole),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.roles, workspaceId, monRole],
  )
}
